import { BROCHURE_LIMIT } from 'app/constants/EventsConstants';
import EventsApi from '../api/EventsApi';
import EventsConstants from '../constants/EventsConstants';
import composeMessaging from '../helpers/messaging/composeMessaging';
import composeGlobalSuccessMessaging from '../helpers/messaging/composeGlobalSuccessMessaging';
import { STATUS_TYPES, GLOBAL_MESSAGING_ID } from '../constants/MessagingConstants';

const EXISTING_REGISTRATION = 'EXISTING_REGISTRATION';
const CONFLICT_ERROR = 'Error: Conflict';

function searchEvents(query = {}) {
  return {
    query,
    types: [
      EventsConstants.SEARCH_EVENTS_REQUEST,
      EventsConstants.SEARCH_EVENTS_SUCCESS,
      EventsConstants.SEARCH_EVENTS_FAILURE
    ],
    apiRequest: (state, client) => EventsApi.searchEvents({ ...query, limit: 20 }, client)
  };
}

function searchEventsByProgram({ programId, eventId, limit = 4 }) {
  return {
    programId,
    eventId,
    limit,
    types: [
      EventsConstants.SEARCH_EVENTS_BY_PROGRAM_REQUEST,
      EventsConstants.SEARCH_EVENTS_BY_PROGRAM_SUCCESS,
      EventsConstants.SEARCH_EVENTS_BY_PROGRAM_FAILURE
    ],
    apiRequest: (state, client) =>
      EventsApi.searchEvents(
        {
          programs: programId,
          excludeEventId: eventId,
          limit
        },
        client
      )
  };
}

function searchEventsByAudienceAndLocation({ audienceId, locationId, eventId, limit = 4 }) {
  return {
    audienceId,
    locationId,
    eventId,
    limit,
    types: [
      EventsConstants.SEARCH_EVENTS_BY_AUDIENCE_REQUEST,
      EventsConstants.SEARCH_EVENTS_BY_AUDIENCE_SUCCESS,
      EventsConstants.SEARCH_EVENTS_BY_AUDIENCE_FAILURE
    ],
    apiRequest: (state, client) =>
      EventsApi.searchEvents(
        {
          audiences: audienceId,
          locations: locationId,
          excludeEventId: eventId,
          limit
        },
        client
      )
  };
}

function searchEventsBySeries({ seriesId, eventId, limit = 3 }) {
  return {
    seriesId,
    eventId,
    limit,
    types: [
      EventsConstants.SEARCH_EVENTS_BY_SERIES_REQUEST,
      EventsConstants.SEARCH_EVENTS_BY_SERIES_SUCCESS,
      EventsConstants.SEARCH_EVENTS_BY_SERIES_FAILURE
    ],
    apiRequest: (state, client) => EventsApi.searchEvents({ series: seriesId, excludeEventId: eventId, limit }, client)
  };
}

function getCalloutFeaturedEvent() {
  return {
    types: [
      EventsConstants.FETCH_CALLOUT_FEATURED_EVENT_REQUEST,
      EventsConstants.FETCH_CALLOUT_FEATURED_EVENT_SUCCESS,
      EventsConstants.FETCH_CALLOUT_FEATURED_EVENT_FAILURE
    ],
    apiRequest: (state, client) =>
      EventsApi.getEvents(
        {
          featured: true,
          cancelled: false,
          full: false,
          limit: 1,
          sort: 'definition.start asc'
        },
        client
      )
  };
}

function getEventById(eventId) {
  return {
    types: [
      EventsConstants.EVENT_BY_ID_REQUEST,
      EventsConstants.EVENT_BY_ID_SUCCESS,
      EventsConstants.EVENT_BY_ID_FAILURE
    ],
    apiRequest: (state, client) => EventsApi.getEventById(eventId, client)
  };
}

function getMyEvents(query = {}) {
  return {
    query,
    types: [EventsConstants.MY_EVENTS_REQUEST, EventsConstants.MY_EVENTS_SUCCESS, EventsConstants.MY_EVENTS_FAILURE],
    middlewareData: {
      authRequired: true
    },
    apiRequest: (state, client) => {
      return EventsApi.getMyEvents({ ...query, limit: 20 }, client);
    }
  };
}

function cancelRegistrationSuccess(removeFromWaitlist, registrationId) {
  if (removeFromWaitlist) {
    return {
      messaging: composeGlobalSuccessMessaging('registration.remove_waitlist.success_toast_message')
    };
  }
  return {
    messaging: composeGlobalSuccessMessaging('registration.cancel.success_toast_message'),
    deletedEntities: [`eventRegistration.${registrationId}`]
  };
}

function cancelRegistration({ registrationId, removeFromWaitlist, postSuccess }) {
  return {
    registrationId,
    removeFromWaitlist,
    postSuccess,
    types: [
      EventsConstants.EVENT_REGISTRATION_CANCEL_REQUEST,
      EventsConstants.EVENT_REGISTRATION_CANCEL_SUCCESS,
      EventsConstants.EVENT_REGISTRATION_CANCEL_FAILURE
    ],
    middlewareData: {
      authRequired: true
    },
    apiRequest: (state, client) => EventsApi.cancelRegistration({ registrationId }, client),
    apiSuccess: () => cancelRegistrationSuccess(removeFromWaitlist, registrationId)
  };
}

function removeFromMyEvents({ registrationId, postSuccess }) {
  return {
    registrationId,
    postSuccess,
    types: [
      EventsConstants.EVENT_REGISTRATION_CANCEL_REQUEST,
      EventsConstants.EVENT_REGISTRATION_CANCEL_SUCCESS,
      EventsConstants.EVENT_REGISTRATION_CANCEL_FAILURE
    ],
    middlewareData: {
      authRequired: true
    },
    apiRequest: (state, client) => EventsApi.updateRegistration({ registrationId, remove: true }, client),
    apiSuccess: () => ({
      messaging: composeGlobalSuccessMessaging('registration.remove_from_my_events.success_toast_message')
    })
  };
}

function fetchEventsForBrochure(query = {}) {
  return {
    query,
    types: [
      EventsConstants.BROCHURE_EVENTS_REQUEST,
      EventsConstants.BROCHURE_EVENTS_SUCCESS,
      EventsConstants.BROCHURE_EVENTS_FAILURE
    ],
    apiRequest: (state, client) => EventsApi.fetchEventsForBrochure({ ...query, limit: BROCHURE_LIMIT }, client)
  };
}

function addToInterestedEvents(query = {}) {
  query.interested = true;
  return {
    query,
    types: [
      EventsConstants.ADD_INTERESTED_EVENTS_REQUEST,
      EventsConstants.ADD_INTERESTED_EVENTS_SUCCESS,
      EventsConstants.ADD_INTERESTED_EVENTS_FAILURE
    ],
    middlewareData: {
      authRequired: true
    },
    apiRequest: (state, client) => EventsApi.addToInterested({ ...query }, client),
    apiSuccess: () => ({
      messaging: composeGlobalSuccessMessaging('registration.add_to_interested.success_toast_message')
    }),
    apiFailure: response => {
      const { meta } = response.error;
      if (meta?.classification === EXISTING_REGISTRATION) {
        return {
          messaging: composeMessaging(
            STATUS_TYPES.Failure,
            GLOBAL_MESSAGING_ID,
            'registration.add_to_interested.failure_toast_message_existing'
          )
        };
      }
      return null;
    }
  };
}

function getMyInterestedEvents(query = {}) {
  return {
    query,
    types: [
      EventsConstants.FETCH_INTERESTED_EVENTS_REQUEST,
      EventsConstants.FETCH_INTERESTED_EVENTS_SUCCESS,
      EventsConstants.FETCH_INTERESTED_EVENTS_FAILURE
    ],
    middlewareData: {
      authRequired: true
    },
    apiRequest: (state, client) => {
      return EventsApi.getMyInterestedEvents({ ...query }, client);
    }
  };
}

function removeFromInterestedEvents(query = {}) {
  query.interested = false;
  return {
    query,
    types: [
      EventsConstants.REMOVE_INTERESTED_EVENTS_REQUEST,
      EventsConstants.REMOVE_INTERESTED_EVENTS_SUCCESS,
      EventsConstants.REMOVE_INTERESTED_EVENTS_FAILURE
    ],
    middlewareData: {
      authRequired: true
    },
    apiRequest: (state, client) => EventsApi.updateRegistration({ ...query }, client),
    apiSuccess: () => ({
      messaging: composeGlobalSuccessMessaging('registration.remove_from_my_events.success_toast_message'),
      deletedEntities: [`eventRegistration.${query.registrationId}`]
    })
  };
}

function createRegistration({ query, postSuccess, postFailure }) {
  return {
    query,
    postSuccess,
    postFailure,
    types: [
      EventsConstants.EVENT_REGISTRATION_REQUEST,
      EventsConstants.EVENT_REGISTRATION_SUCCESS,
      EventsConstants.EVENT_REGISTRATION_FAILURE
    ],
    apiRequest: (state, client) => EventsApi.createRegistration(query, client),
    apiFailure: response => {
      const err = response.error;
      if (err?.message === CONFLICT_ERROR) {
        postFailure(err);
        return { messaging: null };
      }
      return null;
    },
    apiSuccess: () => ({
      messaging:
        query.status === 'waitlist'
          ? composeGlobalSuccessMessaging('event.registration.waitlist.success')
          : composeGlobalSuccessMessaging('event.registration.success')
    })
  };
}

function resendConfirmationEmail(query) {
  return {
    query,
    types: [
      EventsConstants.EVENT_REGISTRATION_CONFIRMATION_REQUEST,
      EventsConstants.EVENT_REGISTRATION_CONFIRMATION_SUCCESS,
      EventsConstants.EVENT_REGISTRATION_CONFIRMATION_FAILURE
    ],
    apiRequest: (state, client) => EventsApi.resendConfirmationEmail(query, client),
    apiSuccess: () => ({
      messaging: composeGlobalSuccessMessaging('events.registration.confirmation')
    })
  };
}

function getEventsSettings() {
  return {
    types: [
      EventsConstants.EVENTS_SETTINGS_REQUEST,
      EventsConstants.EVENTS_SETTINGS_SUCCESS,
      EventsConstants.EVENTS_SETTINGS_FAILURE
    ],
    apiRequest: (state, client) => EventsApi.getEventsSettings(client)
  };
}

export default {
  addToInterestedEvents,
  cancelRegistration,
  createRegistration,
  fetchEventsForBrochure,
  getCalloutFeaturedEvent,
  getEventById,
  getMyEvents,
  getMyInterestedEvents,
  removeFromMyEvents,
  removeFromInterestedEvents,
  searchEvents,
  searchEventsByAudienceAndLocation,
  searchEventsByProgram,
  searchEventsBySeries,
  getEventsSettings,
  resendConfirmationEmail
};
